<template>
  <div class="outer-container">
    <div class="button-group">
      <ui-popover
        v-if="isDocumentValid && hasFailedChecks"
        :open.sync="approveConfirmationVisible"
      >
        <div class="approve-confirm-content">
          <strong>
            {{ approvePopoverHeader }}
          </strong>
          <ul>
            <li v-for="item in failedChecks" :key="item.key" class="list-item">
              <checks-item :item="item" />
            </li>
          </ul>
          <div class="confirmation-button-group">
            <ui-button
              variant="danger"
              @click="approveConfirmationVisible = false"
            >
              {{ $t("identification_approve_cancel") }}
            </ui-button>
            <ui-button v-if="isApproveFinalAvailable" @click="approveFinal">
              {{ $t("identification_approve") }}
            </ui-button>
          </div>
        </div>
        <ui-button
          slot="reference"
          :disabled="
            actionInProgress || isDocumentEditEnabled || isPOAEditEnabled
          "
          variant="success"
          class="approve-btn"
          @click="approveConfirmationVisible = !approveConfirmationVisible"
        >
          {{ $t("identification_approve") }}
        </ui-button>
      </ui-popover>
      <ui-button
        v-else
        @click="approveFinal"
        :disabled="actionInProgress"
        variant="success"
        class="approve-btn"
      >
        {{ $t("identification_approve") }}
      </ui-button>
      <ui-button
        v-if="!isWorkingWithComplianceReview"
        @click="rerouteIdentification"
        :disabled="actionInProgress"
        variant="warning"
      >
        {{ $t("identification_reroute") }}
      </ui-button>
      <ui-button
        v-if="isWorkingWithComplianceReview"
        @click="handleNoChangesAfterComplianceReview"
        :disabled="actionInProgress"
        variant="warning"
      >
        {{ $t("identification_no_changes") }}
      </ui-button>
      <div class="reject-select">
        <ui-select
          @input="reject"
          center
          :value="''"
          placeholder="Reject"
          :disabled="actionInProgress"
          :options="rejectOptions"
          button-like
          variant="danger"
        />
      </div>
    </div>
    <div v-if="isWorkingWithComplianceReview">
      <ui-field-text-area
        id="compliance-review-submission-comment"
        label-position="top"
        bottom-margin="small"
        rows="3"
        v-model="submissionComment"
        :label="$t('identification_submission_review_comment')"
        :disabled="actionInProgress"
        :class="'submission-comment'"
      />
      <div v-if="commentRequiredError" class="error-text">
        {{ commentRequiredError }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { actions, getters } from "@/store";
import { REJECT_REASON, IDENTIFICATION_STATUS } from "@/constants";
import { RESOLUTION_STATUS } from "@/constants/complianceReview";
import { DeadEndFailedFraudChecks } from "@/constants/failedChecks";
import { CHECK_STATUS, ROUTE_NAMES } from "@/router/constants";
import { getDocumentValidation } from "@/utils/form";
import { getDocumentFieldError } from "@/utils/validations";
import { getReasonsLabels } from "@/utils/checks";
import { AppNotification } from "@/constants/appNotification";
import { hasIdentityDocument } from "@/utils/media";
import ChecksItem from "./ChecksItem.vue";

export default {
  props: {
    complianceReviewIdentificationId: { type: String, default: null }
  },
  data() {
    return {
      approveConfirmationVisible: false,
      submissionComment: null,
      commentRequiredError: null
    };
  },
  components: { ChecksItem },
  computed: {
    ...mapGetters([
      getters.currentIdentification,
      getters.actionInProgress,
      getters.isPOAEditEnabled,
      getters.currentDocumentData,
      getters.currentDocumentValidity,
      getters.isDocumentEditEnabled,
      getters.identityUserName,
      getters.currentIdentificationStatus,
      getters.currentIdentificationRejectionReason,
      getters.isWorking
    ]),

    complianceReviewId() {
      return this.$route.query.id;
    },

    isEditActive() {
      return !!(this.isDocumentEditEnabled || this.isPOAEditEnabled);
    },

    doesIdentityDocumentExist() {
      return hasIdentityDocument(this.currentIdentification?.data?.media);
    },

    isDocumentValid() {
      if (!this.doesIdentityDocumentExist) {
        return true;
      }

      const document = this.currentDocumentData;
      if (!document) {
        return false;
      }

      const validation = getDocumentValidation(document);

      return !Object.entries(validation).some(([key]) =>
        getDocumentFieldError(
          key,
          document[key],
          validation[key],
          this.currentDocumentValidity?.[key]
        )
      );
    },

    rejectOptions() {
      return Object.values(REJECT_REASON).map(reason => ({
        label: this.$t(`identification_reject_reasons.${reason}`),
        value: reason
      }));
    },

    failedRuleChecks() {
      return (
        this.currentIdentification?.data?.rules?.reduce((failedRules, rule) => {
          if (
            [CHECK_STATUS.FAIL, CHECK_STATUS.UNAVAILABLE].includes(rule.status)
          ) {
            failedRules.push({
              key: rule.name,
              label: this.$tIf({
                key: `checks_rules.${rule.name}_${rule.status}`
              }),
              reasonLabels: getReasonsLabels(
                rule.reasons,
                "checks_rules_reason",
                this.$tIf
              )
            });
          }
          return failedRules;
        }, []) || []
      );
    },

    failedFraudChecks() {
      return (
        this.currentIdentification?.data?.fraudChecks?.reduce(
          (failedFraudItems, fraudItem) => {
            if (
              [CHECK_STATUS.FAIL, CHECK_STATUS.UNAVAILABLE].includes(
                fraudItem.status
              )
            ) {
              failedFraudItems.push({
                key: fraudItem.name,
                label: this.$tIf({
                  key: `checks_fraud.${fraudItem.name}_${fraudItem.status}`
                }),
                reasonLabels: getReasonsLabels(
                  fraudItem.reasons,
                  "checks_fraud_reason",
                  this.$tIf
                )
              });
            }
            return failedFraudItems;
          },
          []
        ) || []
      );
    },

    registries() {
      return this.currentIdentification?.data?.registries;
    },

    failedRegistryChecks() {
      return (
        this.registries?.data?.reduce((failedRegs, reg) => {
          if (
            [CHECK_STATUS.FAIL, CHECK_STATUS.UNAVAILABLE].includes(reg.status)
          ) {
            failedRegs.push({
              key: reg.name,
              label: this.$tIf({
                key: `checks_registries.${reg.name}_${reg.status}`
              }),
              reasonLabels: getReasonsLabels(
                reg.reasons,
                "checks_registries_reason",
                this.$tIf
              )
            });
          }
          return failedRegs;
        }, []) ?? []
      );
    },

    failedChecks() {
      return [
        ...this.failedRuleChecks,
        ...this.failedRegistryChecks,
        ...this.failedFraudChecks
      ];
    },

    isApproveFinalAvailable() {
      return !this.failedChecks.some(item =>
        [
          DeadEndFailedFraudChecks.StopList,
          DeadEndFailedFraudChecks.BioStopList
        ].includes(item.key)
      );
    },

    approvePopoverHeader() {
      return this.isApproveFinalAvailable
        ? this.$t("identification_approve_checks_failed")
        : this.$t("identification_approve_checks_failed_deadend");
    },

    hasFailedChecks() {
      return this.failedChecks.length > 0;
    },

    isWorkingWithComplianceReview() {
      return !this.isWorking && this.complianceReviewIdentificationId;
    }
  },

  methods: {
    ...mapActions([
      actions.notificationAdd,
      actions.invokeIdentificationReroute,
      actions.putIdentificationApprove,
      actions.putIdentificationReject,
      actions.putResolveComplianceReview
    ]),

    rerouteIdentification() {
      if (this.isEditActive) {
        this.showEditActiveNotifications();
        return;
      }

      this.invokeIdentificationReroute();
    },

    validateComplianceReview() {
      if (!this.submissionComment) {
        this.commentRequiredError = this.$t(
          "compliance_reviews.details.comment_required"
        );

        return false;
      }

      return true;
    },

    async handleResolveComplianceReview(resolution) {
      await this.putResolveComplianceReview({
        id: this.complianceReviewId,
        payload: {
          resolution,
          resolutionComment: this.submissionComment,
          userEmail: this.identityUserName
        }
      });

      this.$router.push({ name: ROUTE_NAMES.COMPLIANCE_REVIEW_SEARCH });
    },

    showEditActiveNotifications() {
      if (this.isDocumentEditEnabled) {
        this.notificationAdd({
          id: AppNotification.DocumentEditActive,
          title: this.$t("identification_document_edit_active"),
          message: this.$t("identification_document_edit_active_suggestion")
        });
      }

      if (this.isPOAEditEnabled) {
        this.notificationAdd({
          id: AppNotification.PoaEditActive,
          title: this.$t("identification_poa_edit_active"),
          message: this.$t("identification_poa_edit_active_suggestion")
        });
      }
    },

    getApproveResolutionStatus() {
      return this.currentIdentificationStatus !==
        IDENTIFICATION_STATUS.MANUAL_FINISH
        ? RESOLUTION_STATUS.STATUS_CHANGED
        : RESOLUTION_STATUS.CONSULTED;
    },

    getRejectResolutionStatus(newRejectionReason, oldRejectionReason) {
      if (this.currentIdentificationStatus !== IDENTIFICATION_STATUS.REJECTED) {
        return RESOLUTION_STATUS.STATUS_CHANGED;
      }

      if (newRejectionReason === oldRejectionReason) {
        return RESOLUTION_STATUS.CONSULTED;
      }

      return RESOLUTION_STATUS.REASON_CHANGED;
    },

    async identificationApprovePut() {
      const isSuccess = await this.putIdentificationApprove({
        identificationId: this.currentIdentification?.identificationId
      });

      this.approveConfirmationVisible = false;

      return isSuccess;
    },

    async approveFinal() {
      if (this.isEditActive) {
        this.showEditActiveNotifications();
        return;
      }

      if (!this.isDocumentValid) {
        this.notificationAdd({
          id: AppNotification.ApproveInvalidDocument,
          title: this.$t("identification_missing_doc"),
          message: this.$t("identification_missing_doc_suggestion")
        });

        return;
      }

      if (this.isWorkingWithComplianceReview) {
        const resolutionStatus = this.getApproveResolutionStatus();

        const isValid = this.validateComplianceReview();

        if (!isValid || resolutionStatus === RESOLUTION_STATUS.CONSULTED) {
          return;
        }

        const isApproveSuccess = await this.identificationApprovePut();

        if (isApproveSuccess) {
          await this.handleResolveComplianceReview(resolutionStatus);
        }

        return;
      }

      this.identificationApprovePut();
    },

    async identificationRejectPut(value) {
      const isSuccess = await this.putIdentificationReject({
        identificationId: this.currentIdentification?.identificationId,
        reason: value
      });

      return isSuccess;
    },

    async reject(value) {
      if (this.isEditActive) {
        this.showEditActiveNotifications();
        return;
      }

      if (this.isWorkingWithComplianceReview) {
        const identificationRejectionReason =
          REJECT_REASON[this.currentIdentificationRejectionReason];

        const resolutionStatus = this.getRejectResolutionStatus(
          value,
          identificationRejectionReason
        );

        const isValid = this.validateComplianceReview();

        if (!isValid || resolutionStatus === RESOLUTION_STATUS.CONSULTED) {
          return;
        }

        const isRejectSuccess = await this.identificationRejectPut(value);

        if (isRejectSuccess) {
          await this.handleResolveComplianceReview(resolutionStatus);
        }

        return;
      }

      this.identificationRejectPut(value);
    },

    async handleNoChangesAfterComplianceReview() {
      const isValid = this.validateComplianceReview();

      if (!isValid) {
        return;
      }

      await this.handleResolveComplianceReview(RESOLUTION_STATUS.CONSULTED);
    }
  }
};
</script>
<style scoped>
.outer-container {
  width: 100%;
}

.list-item {
  margin-bottom: 0.5rem;
}
.button-group {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 0.5rem;
  width: 100%;
}
.confirmation-button-group {
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 0.5rem;
}
.approve-confirm-content {
  box-sizing: border-box;
  max-width: 50rem;
  max-height: 100%;
  padding: 1rem;
  overflow: auto;
  word-break: normal;
  background-color: var(--card-background);
  border-radius: 0.3rem;
  box-shadow: 0 0 0.6rem var(--shadow-color);
}
.approve-btn {
  width: 100%;
}
.submission-comment {
  margin-top: 1rem;
}
.error-text {
  color: var(--red);
}
</style>
